import { useMemo, useState } from 'react';

import { MultiFieldFilter, SearchBar } from '@components/index';
import {
  FieldOptionObj,
  SelectedFilterValues
} from '@components/multi-field-filter/types';
import { CustomField } from '@features/transactions/types';
import { IdLabelValue } from '@types';

import { fieldOptions } from '../constants/leadList';
import { getDateRangeForType, mapDateRangeOptions } from '../utils/leadList';

type LeadFiltersProps = {
  search: string;
  setSearch: (s: string) => void;
  sourceOptions: IdLabelValue[];
  statusOptions: IdLabelValue[];
  tagOptions: IdLabelValue[];
  selectedSources: string[];
  selectedStatusIds: string[];
  selectedTagIds: string[];
  selectedAssigneeIds: string[];
  setSelectedSources: (s: string[]) => void;
  setSelectedStatusIds: (s: string[]) => void;
  setSelectedTagIds: (s: string[]) => void;
  setSelectedAssigneeIds: (s: string[]) => void;
  assigneeOptions: IdLabelValue[];
  setDateRange: (start: string, end: string) => void;
  setDateRangeType: (type: string) => void;
  dateRangeType: string;
};

const LeadFilters = ({
  search,
  setSearch,
  sourceOptions,
  statusOptions,
  tagOptions,
  selectedSources,
  selectedStatusIds,
  selectedTagIds,
  selectedAssigneeIds,
  setSelectedSources,
  setSelectedStatusIds,
  setSelectedTagIds,
  setSelectedAssigneeIds,
  assigneeOptions,
  setDateRange,
  setDateRangeType,
  dateRangeType
}: LeadFiltersProps) => {
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const selectedFilters: SelectedFilterValues = {
    status: selectedStatusIds,
    source: selectedSources,
    tags: selectedTagIds,
    assignee: selectedAssigneeIds,
    dateRange: dateRangeType ? [dateRangeType] : []
  };

  const filterLists = {
    status: statusOptions,
    source: sourceOptions,
    tags: tagOptions,
    assignee: assigneeOptions,
    dateRange: mapDateRangeOptions()
  };

  // mapping options for each field
  const filteredFieldOptions = useMemo(
    () =>
      Object.entries(fieldOptions).reduce((acc, [key, fld]) => {
        acc[key as keyof typeof fieldOptions] = {
          ...fld,
          options: filterLists[fld.field as keyof typeof filterLists]
        };
        return acc;
      }, {} as FieldOptionObj),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statusOptions, sourceOptions, tagOptions, assigneeOptions]
  );

  const onFilterChange = (filters: SelectedFilterValues) => {
    setSelectedStatusIds(filters.status || []);
    setSelectedSources(filters.source || []);
    setSelectedTagIds(filters.tags || []);
    setSelectedAssigneeIds(filters.assignee || []);
    setDateRangeType(filters.dateRange?.[0] || '');
  };

  const handleFilterChange = (
    filters: SelectedFilterValues,
    customDateRange: any
  ) => {
    // For date range, ensure only one option is selected
    // (radio button behavior)
    if (filters.dateRange && filters.dateRange.length > 1) {
      // Keep only the last selected option
      filters.dateRange = [filters.dateRange[filters.dateRange.length - 1]];
    }

    // Handle date range selection separately
    if (
      !filters.dateRange ||
      filters.dateRange.length === 0 ||
      filters.dateRange[0] === ''
    ) {
      // No date range selected - send empty strings
      setDateRange('', '');
    } else {
      const selectedDateRangeType = filters.dateRange[0];

      // Get the appropriate date range based on selection

      const dateRange = getDateRangeForType(
        selectedDateRangeType,
        customDateRange
      );
      setDateRange(dateRange.startDate, dateRange.endDate);
    }

    onFilterChange(filters);
  };

  const handleSelect = (
    filters: SelectedFilterValues,
    dateRange: CustomField[] | undefined
  ) => {
    // Handle custom date range from calendar
    let customDateRange = null;

    if (dateRange && dateRange.length > 0) {
      const dateField = dateRange.find((field) => field.key === 'dateRange');
      if (dateField && dateField.value) {
        customDateRange = {
          startDate: new Date(dateField.value.startDate),
          endDate: new Date(dateField.value.endDate)
        };
      }
    }
    handleFilterChange(filters, customDateRange);
  };

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-x-10">
        <SearchBar
          placeholder="Search"
          value={search}
          onSearch={setSearch}
          searchBarStyle="w-96"
        />
      </div>
      <div className="flex items-center gap-x-2">
        <MultiFieldFilter
          fieldOptions={filteredFieldOptions}
          handleApplyFilter={handleSelect}
          selectedFilters={selectedFilters}
          modalCustomStyle="h-142"
          type="multi"
          customFields={customFields}
          setCustomFields={setCustomFields}
        />
      </div>
    </div>
  );
};

export default LeadFilters;
